import { useSyncExternalStore } from 'react'
import type { AxiosProgressEvent } from 'axios'

export type UploadPhase = 'uploading' | 'processing'

export interface UploadProgressState {
  phase: UploadPhase
  loaded: number
  total: number
  bytesPerSecond: number
  percent: number
  fileName?: string
  /** 1-based index of current file */
  fileIndex?: number
  totalFiles?: number
  slowConnection: boolean
}

export interface UploadProgressMeta {
  fileName?: string
  fileIndex?: number
  totalFiles?: number
  /** Expected payload size when axios does not report total */
  estimatedTotal?: number
}

const SLOW_THRESHOLD_BPS = 150 * 1024 // 150 KB/s
const SLOW_AFTER_MS = 3000
const SPEED_SAMPLE_WINDOW = 5
const MIN_SAMPLE_INTERVAL_MS = 200

type Listener = () => void

let state: UploadProgressState | null = null
const listeners = new Set<Listener>()

let lastLoaded = 0
let lastSampleAt = 0
let speedSamples: number[] = []
let slowSince: number | null = null
let uploadStartedAt = 0

function emit() {
  listeners.forEach((listener) => listener())
}

function setState(next: UploadProgressState | null) {
  state = next
  emit()
}

function averageSpeed(): number {
  if (speedSamples.length === 0) return 0
  return speedSamples.reduce((sum, value) => sum + value, 0) / speedSamples.length
}

function resetSpeedTracking() {
  lastLoaded = 0
  lastSampleAt = 0
  speedSamples = []
  slowSince = null
  uploadStartedAt = Date.now()
}

export function getFormDataFilesSize(data: FormData): number {
  let size = 0
  for (const value of data.values()) {
    if (value instanceof Blob) {
      size += value.size
    }
  }
  return size
}

export function getFormDataFirstFileName(data: FormData): string | undefined {
  for (const value of data.values()) {
    if (value instanceof File) {
      return value.name
    }
  }
  return undefined
}

export function formDataHasFile(data: FormData): boolean {
  for (const value of data.values()) {
    if (value instanceof Blob) {
      return true
    }
  }
  return false
}

export const uploadProgressStore = {
  getState(): UploadProgressState | null {
    return state
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },

  setMeta(meta: UploadProgressMeta) {
    resetSpeedTracking()
    const total = meta.estimatedTotal ?? state?.total ?? 0
    setState({
      phase: 'uploading',
      loaded: 0,
      total,
      bytesPerSecond: 0,
      percent: 0,
      fileName: meta.fileName ?? state?.fileName,
      fileIndex: meta.fileIndex ?? state?.fileIndex,
      totalFiles: meta.totalFiles ?? state?.totalFiles,
      slowConnection: false
    })
  },

  startFromFormData(data: FormData, meta?: UploadProgressMeta) {
    const estimatedTotal = meta?.estimatedTotal ?? getFormDataFilesSize(data)
    const fileName = meta?.fileName ?? getFormDataFirstFileName(data)
    this.setMeta({
      ...meta,
      estimatedTotal,
      fileName
    })
  },

  handleAxiosProgress(event: AxiosProgressEvent, estimatedTotal?: number) {
    const now = Date.now()
    const loaded = event.loaded
    const total =
      event.total && event.total > 0 ? event.total : (estimatedTotal ?? state?.total ?? 0)

    if (!state) {
      resetSpeedTracking()
      lastLoaded = loaded
      lastSampleAt = now
      setState({
        phase: 'uploading',
        loaded,
        total,
        bytesPerSecond: 0,
        percent: total > 0 ? Math.min(100, (loaded / total) * 100) : 0,
        slowConnection: false
      })
      return
    }

    let bytesPerSecond = state.bytesPerSecond
    if (lastSampleAt === 0) {
      lastLoaded = loaded
      lastSampleAt = now
    } else {
      const elapsedSec = (now - lastSampleAt) / 1000
      if (elapsedSec >= MIN_SAMPLE_INTERVAL_MS / 1000) {
        const instant = Math.max(0, (loaded - lastLoaded) / elapsedSec)
        speedSamples.push(instant)
        if (speedSamples.length > SPEED_SAMPLE_WINDOW) {
          speedSamples.shift()
        }
        bytesPerSecond = averageSpeed()
        lastLoaded = loaded
        lastSampleAt = now
      }
    }

    const uploadDuration = now - uploadStartedAt
    let slowConnection = state.slowConnection
    if (bytesPerSecond > 0 && bytesPerSecond < SLOW_THRESHOLD_BPS && loaded < total) {
      if (slowSince === null) {
        slowSince = now
      } else if (now - slowSince >= SLOW_AFTER_MS && uploadDuration >= SLOW_AFTER_MS) {
        slowConnection = true
      }
    } else if (bytesPerSecond >= SLOW_THRESHOLD_BPS) {
      slowSince = null
      slowConnection = false
    }

    const percent = total > 0 ? Math.min(100, (loaded / total) * 100) : state.percent

    setState({
      ...state,
      phase: 'uploading',
      loaded,
      total,
      bytesPerSecond,
      percent,
      slowConnection
    })
  },

  setProcessing() {
    if (!state) return
    setState({
      ...state,
      phase: 'processing',
      loaded: state.total || state.loaded,
      percent: 100,
      bytesPerSecond: state.bytesPerSecond
    })
  },

  /**
   * Clears progress unless a multi-file batch still has remaining files.
   * Returns true when progress was fully cleared.
   */
  completeCurrentFile(): boolean {
    if (!state?.totalFiles || !state.fileIndex || state.fileIndex >= state.totalFiles) {
      this.clear()
      return true
    }

    // Keep last completed state visible until the caller sets the next file meta.
    setState({
      ...state,
      phase: 'processing',
      loaded: state.total || state.loaded,
      percent: 100,
      slowConnection: false
    })
    return false
  },

  clear() {
    resetSpeedTracking()
    setState(null)
  }
}

export function useUploadProgressState(): UploadProgressState | null {
  return useSyncExternalStore(
    uploadProgressStore.subscribe,
    uploadProgressStore.getState,
    uploadProgressStore.getState
  )
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '0 B'
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`
  return `${Math.round(bytes)} B`
}

export function formatSpeed(bytesPerSecond: number): string {
  if (!Number.isFinite(bytesPerSecond) || bytesPerSecond <= 0) return '—'
  if (bytesPerSecond >= 1024 * 1024) {
    return `${(bytesPerSecond / (1024 * 1024)).toFixed(1)} MB/s`
  }
  return `${Math.round(bytesPerSecond / 1024)} KB/s`
}

export function formatEta(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—'
  if (seconds < 1) return '<1 s'
  if (seconds < 60) return `~${Math.ceil(seconds)} s`
  const minutes = Math.floor(seconds / 60)
  const rest = Math.ceil(seconds % 60)
  return `~${minutes} min ${rest} s`
}
